import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { toast } from 'react-toastify';

const Testing = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);

  // Test Email Form
  const [testEmailForm, setTestEmailForm] = useState({
    sender_email: '',
    recipient_email: '',
    subject: 'Cross-Domain Test Email',
    body: 'Hello! This is a cross-domain email test from the warmup system.\n\nTesting connectivity between different domains with proper super admin credential lookup.\n\nThis email verifies that:\n• Sender email uses correct super admin credentials\n• Cross-domain communication works\n• Dynamic settings are applied\n\nBest regards,\nEmail Warmup System'
  });


  const [results, setResults] = useState({});

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      const response = await apiService.getDomains();
      if (response.data && response.data.success) {
        // Handle nested response structure
        const accounts = response.data.data?.all_accounts || response.data.data?.domains || [];
        setAccounts(accounts);
      } else if (response.data?.all_accounts) {
        // Handle direct response structure
        setAccounts(response.data.all_accounts);
      }
    } catch (error) {
      console.error('Failed to fetch accounts:', error);
    }
  };

  const handleTestEmail = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Use the correct backend endpoint
      const response = await fetch('/api/v1/test-email-send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(testEmailForm)
      });
      
      const result = await response.json();
      setResults(prev => ({ ...prev, testEmail: result }));
      
      if (result.actually_sent) {
        toast.success('✅ Test email sent successfully!');
      } else {
        toast.error('❌ Test email failed to send');
      }
    } catch (error) {
      toast.error('Failed to send test email');
      setResults(prev => ({ ...prev, testEmail: { error: error.message } }));
    } finally {
      setLoading(false);
    }
  };



  return (
    <div className="space-y-6 fade-in pt-16">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Testing Dashboard</h1>
        <p className="text-gray-500">Test various email system functions and monitor performance</p>
      </div>

      <div className="max-w-2xl mx-auto">
        {/* Cross-Domain Email Test */}
        <div className="bg-white rounded-lg p-6 card-shadow">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">📧 Cross-Domain Email Test</h3>
          <div className="mb-4 p-3 bg-blue-50 rounded-md">
            <p className="text-sm text-blue-700">
              💡 <strong>How it works:</strong> Select sender and recipient from your email accounts. 
              The system will automatically find the sender's super admin and use their credentials 
              (Gmail service account or Outlook Graph API) to send the email.
            </p>
          </div>
          <form onSubmit={handleTestEmail} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sender Email</label>
              <select
                value={testEmailForm.sender_email}
                onChange={(e) => setTestEmailForm(prev => ({ ...prev, sender_email: e.target.value }))}
                className="form-select"
                required
              >
                <option value="">Select Sender Email</option>
                {accounts.map(account => (
                  <option key={account.email || account.username} value={account.email || account.username}>
                    {account.email || account.username} ({account.domain}) - {account.isp?.toUpperCase()}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                System will automatically use the correct super admin credentials for this sender
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Recipient Email</label>
              <select
                value={testEmailForm.recipient_email}
                onChange={(e) => setTestEmailForm(prev => ({ ...prev, recipient_email: e.target.value }))}
                className="form-select"
                required
              >
                <option value="">Select Recipient Email</option>
                {accounts.map(account => (
                  <option key={`recipient-${account.email || account.username}`} value={account.email || account.username}>
                    {account.email || account.username} ({account.domain}) - {account.isp?.toUpperCase()}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
              <input
                type="text"
                value={testEmailForm.subject}
                onChange={(e) => setTestEmailForm(prev => ({ ...prev, subject: e.target.value }))}
                className="form-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
              <textarea
                rows="4"
                value={testEmailForm.body}
                onChange={(e) => setTestEmailForm(prev => ({ ...prev, body: e.target.value }))}
                className="form-textarea"
              />
            </div>
            <button type="submit" disabled={loading} className="btn-primary w-full">
              {loading ? 'Sending...' : 'Send Test Email'}
            </button>
          </form>
          
          {results.testEmail && (
            <div className="mt-4 p-4 bg-gray-50 rounded-md">
              <h4 className="font-medium text-gray-900 mb-2">Test Result:</h4>
              {results.testEmail.error ? (
                <div className="text-red-600">
                  ❌ <strong>Email Failed:</strong> {results.testEmail.error}
                </div>
              ) : results.testEmail.actually_sent ? (
                <div className="text-green-600">
                  ✅ <strong>Email Sent Successfully!</strong>
                  <br />📧 From: {results.testEmail.sender}
                  <br />📧 To: {results.testEmail.recipient}
                  <br />🔑 Provider: {results.testEmail.send_result?.provider || 'Unknown'}
                  <br />📝 Message ID: {results.testEmail.send_result?.message_id || 'N/A'}
                  <br />⏰ Sent at: {results.testEmail.timestamp}
                  <br />💡 <em>System automatically used correct super admin credentials</em>
                </div>
              ) : (
                <div className="text-yellow-600">
                  ⚠️ <strong>Email Not Sent:</strong> {results.testEmail.send_result?.error || 'Unknown issue'}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Testing;
